/**
 * Service d'impression et génération PDF des factures - VERSION SIMPLE
 * Basé sur les données des ventes existantes avec couleurs et logo boutique
 */

// ✅ IMPORT: Fonction pour générer le HTML du logo
import { generateBoutiqueLogoHTML } from "../boutiques/BoutiqueLogo";

class InvoicePrintService {

  /**
   * Imprimer une facture avec un layout épuré
   */
  static async printInvoice(sale, boutique) {
    try {
      

      // Créer le HTML de la facture
      const invoiceHTML = this.generateInvoiceHTML(sale, boutique);

      // Ouvrir une nouvelle fenêtre pour l'impression
      const printWindow = window.open('', '_blank', 'width=900,height=700,scrollbars=yes');
      
      if (!printWindow) {
        throw new Error('Popup bloqué. Autorisez les popups pour imprimer.');
      }

      // Écrire le contenu dans la fenêtre
      printWindow.document.open();
      printWindow.document.write(invoiceHTML);
      printWindow.document.close();

      // Attendre que le contenu soit chargé
      await new Promise((resolve) => {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            resolve();
          }, 500);
        };
        setTimeout(resolve, 1000); // Fallback
      });

      return {
        success: true,
        message: `Facture ${sale.saleNumber} envoyée à l'impression`
      };

    } catch (error) {
      console.error('❌ Erreur impression facture:', error);
      return {
        success: false,
        message: error.message || 'Erreur lors de l\'impression'
      };
    }
  }

  /**
   * Télécharger une facture (HTML optimisé)
   */
  static async downloadInvoicePDF(sale, boutique) {
    try {

      const invoiceHTML = this.generateInvoiceHTML(sale, boutique, true);
      
      // Créer un blob HTML
      const blob = new Blob([invoiceHTML], { 
        type: 'text/html;charset=utf-8' 
      });
      
      // Créer un lien de téléchargement
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Facture_${sale.saleNumber}.html`;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      return {
        success: true,
        message: `Facture ${sale.saleNumber} téléchargée. Ouvrez le fichier et utilisez Ctrl+P pour imprimer en PDF.`
      };

    } catch (error) {
      console.error('❌ Erreur génération facture:', error);
      return {
        success: false,
        message: error.message || 'Erreur lors de la génération'
      };
    }
  }

  /**
   * Ouvrir pour génération PDF manuelle
   */
  

  /**
   * Générer le HTML de la facture - VERSION SIMPLE avec couleurs et logo
   */
  static generateInvoiceHTML(sale, boutique, forPDF = false) {
    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' XOF';
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    // ✅ Récupérer les couleurs et logo de la boutique
    const primaryColor = boutique?.primaryColor || boutique?.primary_color || '#0d6efd';
    const secondaryColor = boutique?.secondaryColor || boutique?.secondary_color || '#6c757d';
    const logoUrl = boutique?.logoUrl || boutique?.logo_url || null;
    const boutiqueName = boutique?.name || 'SmartBoutik';
    const boutiqueAddress = boutique?.address || 'Adresse non spécifiée';
    const boutiquePhone = boutique?.phone || boutique?.telephone || null;
    const boutiqueEmail = boutique?.email || null;

    

    return `
<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Facture ${sale.saleNumber}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body {
      font-family: 'Arial', sans-serif;
      font-size: 14px;
      line-height: 1.6;
      color: #333;
      background: white;
      padding: 20px;
      max-width: 800px;
      margin: 0 auto;
    }

    .invoice-container {
      background: white;
      border-radius: 10px;
      box-shadow: 0 0 20px rgba(0,0,0,0.1);
      padding: 30px;
    }

    /* ✅ En-tête avec logo et couleurs boutique */
    .invoice-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 3px solid ${primaryColor};
      padding-bottom: 20px;
      margin-bottom: 30px;
    }

    .logo-section {
      display: flex;
      align-items: center;
    }

    .company-logo {
      height: 60px;
      width: 60px;
      border-radius: 8px;
      object-fit: cover;
      margin-right: 15px;
      border: 2px solid ${primaryColor};
    }

    .company-logo-fallback {
      height: 60px;
      width: 60px;
      border-radius: 8px;
      background: linear-gradient(135deg, ${primaryColor}, ${secondaryColor});
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
      font-weight: bold;
      margin-right: 15px;
    }

    .boutique-name {
      font-size: 24px;
      font-weight: bold;
      color: ${primaryColor};
      margin-bottom: 5px;
    }

    .boutique-info {
      color: 38363692;
      font-size: 12px;
    }

    .invoice-title {
      text-align: right;
      color: ${primaryColor};
    }

    .invoice-title h1 {
      font-size: 28px;
      font-weight: bold;
      margin-bottom: 5px;
    }

    .invoice-number {
      color: #38363692;
      padding: 5px 12px;
      border-radius: 15px;
      font-size: 14px;
      font-weight: bold;
      display: inline-block;
    }

    /* ✅ Sections avec couleurs boutique */
    .invoice-details {
      display: flex;
      justify-content: space-between;
      margin-bottom: 30px;
    }

    .bill-to, .invoice-info {
      flex: 1;
      padding: 20px;
      background: #f8f9fa;
      border-radius: 8px;
      margin: 0 10px;
      border-left: 4px solid ${primaryColor};
    }

    .bill-to h3, .invoice-info h3 {
      color: ${primaryColor};
      font-size: 16px;
      margin-bottom: 10px;
      border-bottom: 2px solid ${primaryColor};
      padding-bottom: 5px;
    }

    /* ✅ Tableau avec couleurs boutique */
    .items-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 30px;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 0 10px rgba(0,0,0,0.1);
    }

    .items-table th {
      background: #38363692;
      color: white;
      padding: 15px 10px;
      text-align: left;
      font-weight: bold;
    }

    .items-table th:last-child,
    .items-table td:last-child {
      text-align: right;
    }

    .items-table th:nth-child(2),
    .items-table td:nth-child(2) {
      text-align: center;
    }

    .items-table td {
      padding: 12px 10px;
      border-bottom: 1px solid #eee;
    }

    .items-table tbody tr:nth-child(even) {
      background: #f9f9f9;
    }

    /* ✅ Section totaux */
    .totals-section {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    }

    .notes-section {
      flex: 1;
      margin-right: 30px;
    }

    .notes-section h4 {
      color: ${primaryColor};
      margin-bottom: 10px;
    }

    .notes-content {
      background: #f8f9fa;
      padding: 15px;
      border-radius: 8px;
      border-left: 4px solid ${primaryColor};
    }

    .totals-table {
      min-width: 300px;
      border-collapse: collapse;
      background: white;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 0 10px rgba(0,0,0,0.05);
    }

    .totals-table td {
      padding: 12px 15px;
      border-bottom: 1px solid #eee;
    }

    .totals-table .total-row {
      background: ${primaryColor};
      color: white;
      font-weight: bold;
      font-size: 16px;
    }

    .payment-status {
      display: inline-block;
      padding: 5px 10px;
      border-radius: 15px;
      font-size: 12px;
      font-weight: bold;
      color: white;
      background: ${sale.isCredit ? '#ffc107' : '#28a745'};
    }

    /* ✅ Footer avec couleurs boutique */
    .footer {
      margin-top: 40px;
      text-align: center;
      padding-top: 20px;
      border-top: 2px solid ${primaryColor};
      color: #666;
      font-size: 12px;
    }

    .footer strong {
      color: ${primaryColor};
    }

    /* ✅ Styles d'impression */
    @media print {
      body {
        margin: 0;
        padding: 0;
      }
      
      .invoice-container {
        box-shadow: none;
        border-radius: 0;
        padding: 15px;
      }

      .items-table,
      .totals-table {
        box-shadow: none;
      }

      /* Forcer l'impression des couleurs */
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
    }

    @page {
      size: A4;
      margin: 15mm;
    }

    /* ✅ Responsive */
    @media (max-width: 768px) {
      .invoice-header {
        flex-direction: column;
        text-align: center;
      }

      .logo-section {
        margin-bottom: 15px;
      }

      .invoice-details {
        flex-direction: column;
      }

      .bill-to, .invoice-info {
        margin: 10px 0;
      }

      .totals-section {
        flex-direction: column;
      }

      .notes-section {
        margin-right: 0;
        margin-bottom: 20px;
      }
    }
  </style>
</head>
<body>
  <div class="invoice-container">
    <!-- ✅ En-tête avec logo et couleurs -->
    <div class="invoice-header">
      <div class="logo-section">
        ${generateBoutiqueLogoHTML(boutique, { width: 60, height: 60 })}
        <div>
          <div class="boutique-name">${boutiqueName}</div>
          <div class="boutique-info">
            ${boutiqueAddress}<br>
            ${boutiquePhone ? `Tél: ${boutiquePhone}<br>` : ''}
            ${boutiqueEmail ? `Email: ${boutiqueEmail}` : ''}
          </div>
        </div>
      </div>
      <div class="invoice-title">
        <h1>FACTURE</h1>
        <div class="invoice-number">${sale.saleNumber}</div>
      </div>
    </div>

    <!-- Informations facture -->
    <div class="invoice-details">
      <div class="bill-to">
        <h3>Facturé à</h3>
        <strong>${sale.customer?.name || sale.customerName || 'Client anonyme'}</strong><br>
        ${(sale.customer?.phone || sale.customerPhone) ? 
          `Tél: ${sale.customer?.phone || sale.customerPhone}<br>` : ''}
        ${(sale.customer?.address || sale.customerAddress) ? 
          `Adresse: ${sale.customer?.address || sale.customerAddress}` : ''}
      </div>
      <div class="invoice-info">
        <h3>Détails de la facture</h3>
        <strong>Date:</strong> ${formatDate(sale.createdAt || sale.saleDate)}<br>
        <strong>Vendeur:</strong> ${sale.seller?.name || sale.sellerName || 'N/A'}<br>
        
      </div>
    </div>

    <!-- Tableau des articles -->
    <table class="items-table">
      <thead>
        <tr>
          <th>Description</th>
          <th>Qté</th>
          <th>Prix unitaire</th>
          <th>Total</th>
        </tr>
      </thead>
      <tbody>
        ${(sale.items || sale.saleItems || []).map(item => `
          <tr>
            <td>${item.productName || item.product_name || item.name || 'Article'}</td>
            <td>${item.quantity || 1}</td>
            <td>${formatCurrency(item.unitPrice || item.unit_price || item.price || 0)}</td>
            <td>${formatCurrency((item.unitPrice || item.unit_price || item.price || 0) * (item.quantity || 1))}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>

    <!-- Totaux et notes -->
    <div class="totals-section">
      ${sale.notes ? `
        <div class="notes-section">
          <h4>Notes</h4>
          <div class="notes-content">
            ${sale.notes}
          </div>
        </div>
      ` : '<div class="notes-section"></div>'}
      
      <table class="totals-table">
        <tr>
          <td>Sous-total:</td>
          <td style="text-align: right;">${formatCurrency((sale.total || 0) + (sale.discount || 0))}</td>
        </tr>
        ${(sale.discount || 0) > 0 ? `
          <tr>
            <td style="color: #dc3545;">Remise:</td>
            <td style="text-align: right; color: #dc3545;">-${formatCurrency(sale.discount)}</td>
          </tr>
        ` : ''}
        <tr class="total-row">
          <td>TOTAL À PAYER:</td>
          <td style="text-align: right;">${formatCurrency(sale.total || 0)}</td>
        </tr>
        <tr>
          <td>Mode de paiement:</td>
          <td style="text-align: right;">
            <span class="payment-status">
              ${sale.isCredit ? 'À crédit' : (sale.paymentMethod || 'Espèces')}
            </span>
          </td>
        </tr>
      </table>
    </div>

    <!-- Footer -->
    <div class="footer">
      <strong>Merci pour votre confiance !</strong><br>
      Cette facture a été générée par <strong style="color: ${primaryColor};">${boutiqueName}</strong><br>
      <small>Généré le ${new Date().toLocaleString('fr-FR')}</small>
    </div>
  </div>

  <script>
    // ✅ Gestion du fallback du logo
    document.addEventListener('DOMContentLoaded', function() {
      const logos = document.querySelectorAll('.company-logo');
      logos.forEach(logo => {
        logo.addEventListener('error', function() {
          this.style.display = 'none';
          const fallback = this.nextElementSibling;
          if (fallback && fallback.classList.contains('company-logo-fallback')) {
            fallback.style.display = 'flex';
          }
        });
      });
    });
  </script>
</body>
</html>`;
  }

  /**
   * Prévisualiser une facture (pour tests)
   */
  static previewInvoice(sale, boutique) {
    const invoiceHTML = this.generateInvoiceHTML(sale, boutique);
    const previewWindow = window.open('', '_blank', 'width=900,height=700,scrollbars=yes,resizable=yes');
    
    if (previewWindow) {
      previewWindow.document.open();
      previewWindow.document.write(invoiceHTML);
      previewWindow.document.close();
      previewWindow.focus();
    } else {
      alert('Popup bloqué. Autorisez les popups pour prévisualiser la facture.');
    }
  }

  /**
   * Fonction de test pour débugger les couleurs et logo
   */
  static testBoutiqueData(boutique) {
    
    
    return {
      hasLogo: !!(boutique?.logoUrl || boutique?.logo_url),
      hasColors: !!(boutique?.primaryColor || boutique?.primary_color),
      isComplete: !!(boutique?.name && (boutique?.primaryColor || boutique?.primary_color))
    };
  }
}

export default InvoicePrintService;